/**
 * Client-side API for admin user management operations
 *
 * All operations are performed server-side via API calls.
 * Use these instead of direct Firestore writes on users collection.
 */

import { apiFetch } from '@/lib/api-client';
import logger from '@/lib/logging';

export interface BulkOperationResult {
  success: number;
  failed: number;
  errors: Array<{ userId: string; error: string }>;
}

/**
 * Bulk soft-delete users
 */
export async function bulkDeleteUsersApi(
  userIds: string[],
  deletedBy: string,
  options?: { confirmSelf?: boolean }
): Promise<BulkOperationResult> {
  try {
    return await apiFetch<BulkOperationResult>('/admin/users/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({
        userIds,
        deletedBy,
        ...(options?.confirmSelf ? { confirmSelf: true } : {}),
      }),
    });
  } catch (error) {
    const errorText = error instanceof Error ? error.message : String(error);
    logger.error('Error calling bulk delete API:', { error, userIds });
    return {
      success: 0,
      failed: userIds.length,
      errors: userIds.map((id) => ({ userId: id, error: errorText })),
    };
  }
}


export interface UpdateUserProfileInput {
  displayName?: string;
  email?: string;
}

/**
 * Update user profile (displayName, email only)
 */
export async function updateUserProfileApi(
  userId: string,
  updates: UpdateUserProfileInput
): Promise<boolean> {
  try {
    await apiFetch(`/admin/users/${encodeURIComponent(userId)}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
    return true;
  } catch (error) {
    logger.error('Error calling update user profile API:', { error, userId });
    return false;
  }
}
